import {takeEvery} from 'redux-saga';
import {call, put} from 'redux-saga/effects';
import reduceReducers from 'reduce-reducers';
import {showQuestion} from './Navigation';
import {appError, busy, ready} from './Status';
import answerReducer from './Question-Answers';
import commentReducer from './Question-Comments';

const meta = {requireAuthentication: true};

const GET_QUESTION = 'app-qa/question/GET_QUESTION';
export const getQuestion = (url, contextType) => ({type: GET_QUESTION, url, contextType});

const GET_QUESTION_AUTO_CREATE = 'app-qa/question/GET_QUESTION_AUTO_CREATE';
export const getQuestionAutoCreate = (context, contextType) =>
  ({type: GET_QUESTION_AUTO_CREATE, context, contextType});

const CREATE_QUESTION = 'app-qa/question/CREATE_QUESTION';
export const createQuestion = (question) => ({type: CREATE_QUESTION, question, meta});

const UPDATE_QUESTION = 'app-qa/question/UPDATE_QUESTION';
export const updateQuestion = (question) => ({type: UPDATE_QUESTION, question, meta});

const DELETE_QUESTION = 'app-qa/question/DELETE_QUESTION';
export const deleteQuestion = (question) => ({type: DELETE_QUESTION, question, meta});

const MODERATE_QUESTION = 'app-qa/question/MODERATE_QUESTION';
export const moderateQuestion = (question) => ({type: MODERATE_QUESTION, question, meta});

const LOCK_QUESTION = 'app-qa/question/LOCK_QUESTION';
export const lockQuestion = (question) => ({type: LOCK_QUESTION, question, meta});

const QUESTION_REQUEST_SUCCESS = 'app-qa/question/QUESTION_REQUEST_SUCCESS';
export const questionRequestSuccess = (question) => ({type: QUESTION_REQUEST_SUCCESS, question});

const GET_QUESTION_VOTES = 'app-qa/question/GET_QUESTION_VOTES';
export const getQuestionVotes = (questionId) => ({type: GET_QUESTION_VOTES, questionId});

const QUESTION_VOTES_REQUEST_SUCCESS = 'app-qa/question/QUESTION_VOTES_REQUEST_SUCCESS';
export const questionVotesRequestSuccess = (questionVotes) => ({type: QUESTION_VOTES_REQUEST_SUCCESS, questionVotes});

const INCREMENT_QUESTION_COUNTER = 'app-qa/question/INCREMENT_QUESTION_COUNTER';
export const incrementQuestionCounter = (increment) =>
  ({type: INCREMENT_QUESTION_COUNTER, increment, counter: 'vote', meta});
export const incrementQuestionViewCounter = (increment) =>
  ({type: INCREMENT_QUESTION_COUNTER, increment, counter: 'view'});

const INCREMENT_QUESTION_COUNTER_SUCCESS = 'app-qa/question/INCREMENT_QUESTION_COUNTER_SUCCESS';
export const incrementQuestionCounterSuccess = (increment, votes) =>
  ({type: INCREMENT_QUESTION_COUNTER_SUCCESS, increment, votes});

const SET_LOADING_QUESTION = 'app-qa/question/SET_LOADING_QUESTION';
export const setLoadingQuestion = (loading) => ({type: SET_LOADING_QUESTION, loading});

const CLEAR_QUESTION = 'app-qa/question/CLEAR_QUESTION';
export const clearQuestion = () => ({type: CLEAR_QUESTION});

export const actions = {
  GET_QUESTION,
  GET_QUESTION_AUTO_CREATE,
  CREATE_QUESTION,
  UPDATE_QUESTION,
  DELETE_QUESTION,
  MODERATE_QUESTION,
  LOCK_QUESTION,
  QUESTION_REQUEST_SUCCESS,
  GET_QUESTION_VOTES,
  QUESTION_VOTES_REQUEST_SUCCESS,
  INCREMENT_QUESTION_COUNTER,
  INCREMENT_QUESTION_COUNTER_SUCCESS,
  SET_LOADING_QUESTION,
  CLEAR_QUESTION,
};

const initialState = {
  loadingQuestion: false,
  questionVotes: {},
};

const reducer = (state = initialState, action = {}) => {
  switch (action.type) {
    case SET_LOADING_QUESTION: {
      return {...state, loadingQuestion: true};
    }
    case CLEAR_QUESTION: {
      return {...initialState};
    }
    case QUESTION_REQUEST_SUCCESS: {
      return {currentQuestion: action.question, questionVotes: {}, loadingQuestion: false};
    }
    case QUESTION_VOTES_REQUEST_SUCCESS: {
      return {...state, questionVotes: action.questionVotes.result};
    }
    case INCREMENT_QUESTION_COUNTER_SUCCESS: {
      const updatedVotes = action.votes.result;
      const questionUpdate = {...state.currentQuestion, ...updatedVotes};
      return {...state, currentQuestion: questionUpdate};
    }
    default:
      return state;
  }
};

export default reduceReducers(reducer, answerReducer, commentReducer);

export function sagas({qaClient}) {
  function *getQuestionSaga({url, contextType}) {
    yield put(setLoadingQuestion(true));
    yield put(busy({blockUi: true}));
    try {
      const question = yield call(() => qaClient.getQuestion(url, contextType));
      yield put(questionRequestSuccess(question));
      yield put(ready());
    } catch (err) {
      yield put(appError({err}));
    }
  }

  function *getQuestionAutoCreateSaga({context, contextType}) {
    yield put(setLoadingQuestion(true));
    yield put(busy({blockUi: true}));
    try {
      const response = yield call(qaClient.getQuestionForContext, context);
      yield put(questionRequestSuccess(response));
      yield put(ready());
    } catch (err) {
      const newQuestion = yield qaClient.createQuestion({
        title: `auto create ${context}`,
        content: `auto created ${context}`,
        context,
        context_type: contextType,
        useTesUser: true,
      });
      const question = yield call(() => qaClient.getQuestion(newQuestion.result.url));
      yield put(questionRequestSuccess(question));
      yield put(ready());
    }
  }

  function *getQuestionVotesSaga({questionId}) {
    try {
      const questionVotes = yield call(() => qaClient.getQuestionVotes(questionId));
      yield put(questionVotesRequestSuccess(questionVotes));
    } catch (err) {
      yield put(appError({err}));
    }
  }

  function *createQuestionSaga({question}) {
    yield put(busy({blockUi: true}));
    try {
      const response = yield call(() => qaClient.createQuestion(question));
      yield put(showQuestion(response.result.url));
      yield put(ready());
    } catch (err) {
      yield put(appError({err}));
    }
  }

  function *updateQuestionSaga({question}) {
    yield put(busy({blockUi: true}));
    try {
      const response = yield call(() => qaClient.updateQuestion(question));
      yield put(getQuestion(response.result.url));
      yield put(showQuestion(response.result.url));
      yield put(ready());
    } catch (err) {
      yield put(appError({err}));
    }
  }

  function *moderateQuestionSaga({question}) {
    yield put(busy({blockUi: true}));
    try {
      const response = yield call(() => qaClient.moderateQuestion(question));
      yield put(questionRequestSuccess(response.result));
      yield put(ready());
    } catch (err) {
      yield put(appError({err}));
    }
  }

  function *deleteQuestionSaga({question}) {
    yield put(busy({blockUi: true}));
    try {
      const response = yield call(() => qaClient.deleteQuestion(question));
      yield put(questionRequestSuccess(response.result));
      yield put(ready());
    } catch (err) {
      yield put(appError({err}));
    }
  }

  function *lockQuestionSaga({question}) {
    yield put(busy({blockUi: true}));
    try {
      const response = yield call(() => qaClient.lockQuestion(question));
      yield put(questionRequestSuccess(response.result));
      yield put(ready());
    } catch (err) {
      yield put(appError({err}));
    }
  }

  function *incrementQuestionCounterSaga({increment, counter}) {
    try {
      const response = yield call(() => qaClient.incrementQuestionCounter(increment, counter));
      yield put(incrementQuestionCounterSuccess(increment, response));
      if (counter === 'vote') {
        yield put(getQuestionVotes(increment.question_id));
      }
    } catch (err) {
      yield put(appError({err}));
    }
  }

  return {
    *watchGetQuestion() { yield* takeEvery(GET_QUESTION, getQuestionSaga); },
    *watchGetQuestionAutoCreate() { yield* takeEvery(GET_QUESTION_AUTO_CREATE, getQuestionAutoCreateSaga); },
    *watchCreateQuestion() { yield* takeEvery(CREATE_QUESTION, createQuestionSaga); },
    *watchUpdateQuestion() { yield* takeEvery(UPDATE_QUESTION, updateQuestionSaga); },
    *watchModerateQuestion() { yield* takeEvery(MODERATE_QUESTION, moderateQuestionSaga); },
    *watchDeleteQuestion() { yield* takeEvery(DELETE_QUESTION, deleteQuestionSaga); },
    *watchLockQuestion() { yield* takeEvery(LOCK_QUESTION, lockQuestionSaga); },
    *watchIncrementQuestionCounter() { yield* takeEvery(INCREMENT_QUESTION_COUNTER, incrementQuestionCounterSaga); },
    *watchGetQuestionVotes() { yield* takeEvery(GET_QUESTION_VOTES, getQuestionVotesSaga); },
  };
}
